// Admin backend — Phase B.
//
// admin_orders_view already joins orders + profiles. Writes go to `orders`
// (except full name → `profiles`). Never touch the view directly.
//
// Enums:
//   service : wash_fold | same_day | dry_cleaning | members
//   slot    : morning | evening
//   status  : booked | picked_up | processing | out_for_delivery
//             | delivered | cancelled
//   ⚠️ 'paid' exists in the enum but is intentionally unused — payment is
//   NOT a status. Use paid_at / is_paid instead.

import { supabase } from './supabase'
import { isoDate } from './dates'
import { notifyPayment } from './telegramAlerts'

// --- Admin check -----------------------------------------------------------

export async function isCurrentUserAdmin() {
  const { data: sess } = await supabase.auth.getSession()
  const uid = sess?.session?.user?.id
  if (!uid) return false
  const { data, error } = await supabase
    .from('admins')
    .select('user_id')
    .eq('user_id', uid)
    .maybeSingle()
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[admin] isCurrentUserAdmin failed', error)
    return false
  }
  return !!data
}

// --- Status metadata -------------------------------------------------------

// Ordered — 'paid' intentionally omitted (not a status).
export const STATUS_FLOW = [
  'booked',
  'picked_up',
  'processing',
  'out_for_delivery',
  'delivered',
  'cancelled',
]

export const STATUS_LABEL = {
  booked: 'Booked',
  picked_up: 'Picked up',
  processing: 'Processing',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

// Design tokens — kept literal to match spec exactly.
export const STATUS_STYLE = {
  booked:           { color: '#D4A017', background: '#FEF9E7' },
  picked_up:        { color: '#0E8CA0', background: '#E8F9FC' },
  processing:       { color: '#FFFFFF', background: '#0B2038' },
  out_for_delivery: { color: '#FFFFFF', background: '#22C55E' },
  delivered:        { color: '#9AA7B4', background: '#F3F4F6' },
  cancelled:        { color: '#FFFFFF', background: '#EF4444' },
}

// --- Queries ---------------------------------------------------------------
//
// Archive rule per spec:
//   archived     = status IN (delivered, cancelled) AND updated_at::date < today
//   still-active = everything else (including delivered/cancelled closed today)
//
// PostgREST can't do `updated_at::date` server-side without a computed
// column, so we filter by a full ISO timestamp — everything with an
// updated_at ≥ start-of-today counts as "closed today", still active.

// eslint-disable-next-line no-unused-vars
function todayStartIso(now = new Date()) {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

const CLOSED_STATUSES = ['delivered', 'cancelled']

export async function listActiveOrders({
  search,
  status,
  service,
  paid, // 'paid' | 'unpaid' | undefined
  date, // iso YYYY-MM-DD filter on created_at
} = {}) {
  let q = supabase.from('admin_orders_view').select('*')

  // A ticket is live until it's delivered or cancelled — payment never
  // closes it. `delivered_at is null` also rescues a row stamped
  // `delivered` without a timestamp, which would otherwise be invisible.
  q = q.or(
    `status.not.in.(${CLOSED_STATUSES.join(',')}),` +
    `and(status.eq.delivered,delivered_at.is.null)`,
  )

  if (status) q = q.eq('status', status)
  if (service) q = q.eq('service', service)
  if (paid === 'paid') q = q.eq('is_paid', true)
  if (paid === 'unpaid') q = q.eq('is_paid', false)
  if (date) {
    const from = `${date}T00:00:00.000Z`
    const to = `${date}T23:59:59.999Z`
    q = q.gte('created_at', from).lte('created_at', to)
  }
  if (search) {
    const s = String(search).trim().replace(/[,()]/g, '')
    q = q.or(
      [
        `order_number.ilike.%${s}%`,
        `customer_name.ilike.%${s}%`,
        `customer_phone.ilike.%${s}%`,
      ].join(','),
    )
  }

  q = q.order('is_paid', { ascending: true, nullsFirst: false })
       .order('status_sort', { ascending: true, nullsFirst: false }) // no-op if col absent
       .order('created_at', { ascending: false })

  const { data, error } = await q
  if (error) throw error
  return data || []
}

// History — every closed ticket, including the ones closed today (they leave
// the board the moment they're delivered, so History has to catch them right
// away or they'd be invisible until tomorrow).
export async function listArchivedOrders({
  search,
  status,
  service,
  paid,
  date,
} = {}) {
  let q = supabase.from('admin_orders_view').select('*')
  q = q.in('status', CLOSED_STATUSES)

  if (status && CLOSED_STATUSES.includes(status)) q = q.eq('status', status)
  if (service) q = q.eq('service', service)
  if (paid === 'paid') q = q.eq('is_paid', true)
  if (paid === 'unpaid') q = q.eq('is_paid', false)
  if (date) {
    const from = `${date}T00:00:00.000Z`
    const to = `${date}T23:59:59.999Z`
    q = q.gte('created_at', from).lte('created_at', to)
  }
  if (search) {
    const s = String(search).trim().replace(/[,()]/g, '')
    q = q.or(
      [
        `order_number.ilike.%${s}%`,
        `customer_name.ilike.%${s}%`,
        `customer_phone.ilike.%${s}%`,
      ].join(','),
    )
  }
  q = q.order('updated_at', { ascending: false })
  const { data, error } = await q
  if (error) throw error
  return data || []
}

export async function countArchivedOrders() {
  const { count, error } = await supabase
    .from('admin_orders_view')
    .select('*', { count: 'exact', head: true })
    .in('status', CLOSED_STATUSES)
  if (error) throw error
  return count || 0
}

// Today tab kept unchanged — same shape as before.
export async function listToday(dateIso) {
  const [pickups, deliveries] = await Promise.all([
    supabase
      .from('admin_orders_view')
      .select('*')
      .eq('pickup_date', dateIso)
      .order('pickup_slot', { ascending: true })
      .order('created_at', { ascending: true }),
    supabase
      .from('admin_orders_view')
      .select('*')
      .eq('return_date', dateIso)
      .order('return_slot', { ascending: true })
      .order('created_at', { ascending: true }),
  ])
  if (pickups.error) throw pickups.error
  if (deliveries.error) throw deliveries.error
  return { pickups: pickups.data || [], deliveries: deliveries.data || [] }
}

export async function getOrderForAdmin(orderNumber) {
  if (!orderNumber) return null
  const { data, error } = await supabase
    .from('admin_orders_view')
    .select('*')
    .eq('order_number', orderNumber)
    .maybeSingle()
  if (error) throw error
  return data
}

// --- Mutations -------------------------------------------------------------

// Generic order patch — writes to the orders table (not the view).
// Always touches updated_at so the archive rule kicks in on the next
// refresh.
export async function patchOrder(orderId, patch) {
  const payload = { ...patch, updated_at: new Date().toISOString() }
  const { data, error } = await supabase
    .from('orders')
    .update(payload)
    .eq('id', orderId)
    .select()
    .single()
  if (error) throw error
  // Telegram alert 4 — the single client-side trigger for a payment: any
  // admin path that writes paid_at (Mark as paid, the paid toggle) goes
  // through here. Un-marking (paid_at → null) sends nothing, and the
  // telegram_alerts_sent row keeps re-marking from alerting twice.
  if (patch?.paid_at) notifyPayment(orderId)
  return data
}

// Wraps the Postgres RPC that debits prepaid lbs for an order (spec §7ter).
// Idempotent: safe to call on every Save. Non-member / cancelled-member
// orders short-circuit to `{ cycle_used: 0, prepaid_used: 0, overage_lbs,
// balance_after: null }` without touching any balance.
export async function consumePrepaidLbsForOrder(orderId) {
  if (!orderId) return null
  const { data, error } = await supabase
    .rpc('consume_prepaid_lbs_for_order', { p_order_id: orderId })
  if (error) throw error
  // RPC returns TABLE(...) — supabase-js gives us an array of rows.
  const row = Array.isArray(data) ? data[0] : data
  return row || null
}

// Find an active order to attach a new one to ("Add to existing pickup").
// Matches on order number or customer name, newest first.
export async function searchOrdersForPickup(term) {
  const s = String(term || '').trim().replace(/[,()]/g, '')
  if (s.length < 2) return []
  const { data, error } = await supabase
    .from('admin_orders_view')
    .select('id, order_number, customer_name, service, service_name, pickup_date, pickup_slot, status, pickup_group_id')
    .in('status', ['booked', 'picked_up', 'processing'])
    .or([`order_number.ilike.%${s}%`, `customer_name.ilike.%${s}%`].join(','))
    .order('created_at', { ascending: false })
    .limit(8)
  if (error) throw error
  return data || []
}

// Join a just-created order to an existing pickup. Server-side: it creates
// the group if needed and flips the whole group to "return TBD". It never
// touches the service of the order already there.
export async function attachOrderToPickup(orderId, targetOrderId) {
  const { data, error } = await supabase.rpc('attach_order_to_pickup', {
    p_order_id: orderId,
    p_target_order_id: targetOrderId,
  })
  if (error) throw error
  return data
}

// Add a service to a pickup straight from the ticket. The client sends two
// ids and a bag count — never a price, an address or a customer. The RPC
// copies the stop from the order already there, creates the group if the
// ticket was still a lone order, and flips the whole pickup to "return TBD".
export async function addServiceToPickup(orderId, serviceId, bags = 1) {
  const { data, error } = await supabase.rpc('add_service_to_pickup', {
    p_order_id: orderId,
    p_service_id: serviceId,
    p_bags: Number(bags) || 1,
  })
  if (error) throw error
  return data
}

// Take a service back out of a pickup (cancels that order and detaches it).
// Without it, one order that never gets weighed blocks the invoice for the
// whole pickup with no way out. Refused once that order is invoiced or paid.
export async function removeServiceFromPickup(orderId) {
  const { data, error } = await supabase.rpc('remove_service_from_pickup', {
    p_order_id: orderId,
  })
  if (error) throw error
  return data
}

// The customer's usable coupon, if any: not redeemed, not expired.
// Read-only — minting and redeeming both happen server-side.
export async function activeCouponFor(profileId) {
  if (!profileId) return null
  const { data, error } = await supabase
    .from('coupons')
    .select('id, code, kind, amount_cents, bonus_lbs, expires_at')
    .eq('profile_id', profileId)
    .is('redeemed_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: true })
    .limit(1)
  if (error) throw error
  return data?.[0] || null
}

// Apply a coupon to a ticket. The RPC checks ownership, expiry and single
// use, then writes the discount and marks the coupon redeemed in one go —
// the same coupon can never land on two orders.
export async function redeemCouponForOrder(orderId, code) {
  const { data, error } = await supabase.rpc('redeem_coupon_for_order', {
    p_order_id: orderId,
    p_code: code,
  })
  if (error) throw error
  return data
}

// Every order sharing a pickup. Returns [] when the order isn't grouped —
// callers can then keep their single-order path untouched.
export async function listPickupGroup(groupId) {
  if (!groupId) return []
  const { data, error } = await supabase
    .from('admin_orders_view')
    .select('*')
    .eq('pickup_group_id', groupId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return data || []
}

// One Save for a whole pickup: the RPC writes every order in a single
// transaction, so a group can never end up half-saved.
export async function savePickupGroupWeights(rows) {
  const { data, error } = await supabase.rpc('save_pickup_group_weights', { p_rows: rows })
  if (error) throw error
  return data || []
}

// Mark every order of a pickup paid in one go — one pickup, one payment.
export async function patchPickupGroup(groupId, patch) {
  const payload = { ...patch, updated_at: new Date().toISOString() }
  const { data, error } = await supabase
    .from('orders')
    .update(payload)
    .eq('pickup_group_id', groupId)
    .select()
  if (error) throw error
  return data || []
}

export function updateStatus(orderId, status) {
  return patchOrder(orderId, { status })
}

export function setWeighAndTotal(orderId, weighedLbs, finalTotalCents) {
  return patchOrder(orderId, {
    weighed_lbs: weighedLbs,
    final_total_cents: finalTotalCents,
  })
}

// Payment toggle — writes paid_at only. is_paid is a generated column so
// the client just reads it back.
export async function togglePaid(orderId, currentlyPaid) {
  return patchOrder(orderId, { paid_at: currentlyPaid ? null : new Date().toISOString() })
}

export async function reopenOrder(orderId) {
  return patchOrder(orderId, { status: 'out_for_delivery' })
}

// Customer name — writes to profiles, since the customer_name column comes
// from the joined view. We split on the last space so common patterns
// (single, double, or hyphenated last names) survive a round-trip.
export async function updateCustomerName(userId, fullName) {
  const trimmed = String(fullName || '').trim().replace(/\s+/g, ' ')
  const lastSpace = trimmed.lastIndexOf(' ')
  const first = lastSpace > 0 ? trimmed.slice(0, lastSpace) : trimmed
  const last = lastSpace > 0 ? trimmed.slice(lastSpace + 1) : ''
  const payload = {
    first_name: first,
    last_name: last,
    full_name: trimmed || null,
    updated_at: new Date().toISOString(),
  }
  // Try with full_name column; if the DB doesn't have it, retry without.
  let { data, error } = await supabase
    .from('profiles')
    .update(payload)
    .eq('id', userId)
    .select()
    .maybeSingle()
  if (error && /full_name/i.test(error.message || '')) {
    delete payload.full_name
    ;({ data, error } = await supabase
      .from('profiles')
      .update(payload)
      .eq('id', userId)
      .select()
      .maybeSingle())
  }
  if (error) throw error
  return data
}

// --- Pricing helpers -------------------------------------------------------
// Removed: `calcTotal` used the wrong formula (billed max(lbs, included) ×
// extra_rate, over-charging every order). All pricing now flows through
// src/lib/pricing.js quote() which reads the flat base + line items.

export { fmtMoney } from './pricing'

export function todayIso() {
  return isoDate(new Date())
}

// ── B.3 — Admin search customers + create guest profile / order ──────────

// Admin-only fuzzy search on profiles by name, phone, or email.
// Returns up to 10 matches, members first, then normal customers, then
// guests. Requires the caller to hit the RLS `profiles_admin_read` policy
// (i.e. is_admin() = true).
export async function searchCustomers(query, { limit = 10 } = {}) {
  const q = String(query || '').trim()
  if (q.length < 2) return []
  const like = `%${q}%`
  // Split so name search hits the two columns; phone stays literal.
  const or = [
    `first_name.ilike.${like}`,
    `last_name.ilike.${like}`,
    `email.ilike.${like}`,
    `phone.ilike.${like}`,
  ].join(',')
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, email, phone, address_line1, city, state, zip, hotel_name, room_number, is_member, member_status, is_guest')
    .or(or)
    .limit(limit)
  if (error) throw error
  const rows = data || []
  rows.sort((a, b) => {
    // Members active first, then normal customers, then guests.
    const rank = (r) => (r.member_status === 'active' ? 0 : r.is_guest ? 2 : 1)
    return rank(a) - rank(b)
  })
  return rows
}

// Creates a guest profile — is_guest = true, no auth.users row.
// The random UUID is generated by Postgres (default gen_random_uuid() on
// the id column via .insert without id ⇒ picks the default). Guarded by
// the `profiles_admin_insert` policy (is_admin()).
export async function createGuestProfile(input) {
  const { data, error } = await supabase
    .from('profiles')
    .insert({
      first_name: input.first_name || null,
      last_name: input.last_name || null,
      email: input.email || null,
      phone: input.phone || null,
      address_line1: input.address_line1 || null,
      address_line2: input.address_line2 || null,
      city: input.city || null,
      state: input.state || null,
      zip: input.zip || null,
      hotel_name: input.hotel_name || null,
      room_number: input.room_number || null,
      is_guest: true,
    })
    .select()
    .single()
  if (error) throw error
  return data
}

// Same payload shape as the client-side saveOrder(), plus explicit user_id.
// Admins can insert with any user_id (orders_admin_all policy is '*').
export async function adminCreateOrder(order) {
  const payload = {
    user_id: order.user_id,
    service: order.service,
    ...(order.service_id ? { service_id: order.service_id } : {}),
    bags: String(order.bags || '1'),
    pickup_date: order.pickupDate,
    pickup_slot: order.pickupSlot,
    return_date: order.returnDate || null,
    return_slot: order.returnSlot || null,
    return_tbd: !!order.returnDeferred,
    address_line1: order.address?.street || null,
    address_line2: order.address?.apt || null,
    city: order.address?.city || null,
    state: order.address?.state || null,
    zip: order.address?.zip || null,
    hotel_name: order.address?.hotelName || null,
    room_number: order.address?.roomNumber || null,
    notes: order.notes || null,
  }
  const { data, error } = await supabase
    .from('orders')
    .insert(payload)
    .select()
    .single()
  if (error) throw error
  return data
}
